import React from "react";
import { BsImage, BsInfoCircle, BsPencilSquare, BsLockFill } from "react-icons/bs";
import { FiUpload } from "react-icons/fi";
import LexicalEditor from "../common/LexicalEditor";

const categories = [
  { value: "desci_general", label: "DeSci General", icon: "🔬" },
  { value: "biotech_life_sciences", label: "Biotech & Life Sciences", icon: "🧬" },
  { value: "ai_computer_science", label: "AI & Computer Science", icon: "🤖" },
  { value: "open_source_software", label: "Open Source Software", icon: "💻" },
  { value: "women_in_science", label: "Women in Science", icon: "👩‍🔬" },
  { value: "regional", label: "Regional", icon: "🌍" },
  { value: "climate_environment", label: "Climate & Environment", icon: "🌱" },
  { value: "space_astronomy", label: "Space & Astronomy", icon: "🚀" },
  { value: "other", label: "Other", icon: "✨" },
];

const tiers = [
  { value: "bronze", label: "Bronze", color: "from-amber-600 to-amber-400" },
  { value: "silver", label: "Silver", color: "from-gray-400 to-gray-300" },
  { value: "gold", label: "Gold", color: "from-yellow-500 to-yellow-300" },
  { value: "platinum", label: "Platinum", color: "from-slate-600 to-slate-400" },
  { value: "custom", label: "Custom", color: "from-purple-600 to-purple-400" },
];

const Step1BasicInfo = ({ formik, handleImageUpload, uploadingImage, isPublished }) => {
  return (
    <div className="space-y-5 md:space-y-6">
      <div className="text-center mb-6 md:mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-purple-100 to-blue-100 mb-3">
          <BsPencilSquare className="text-2xl md:text-3xl text-primary" />
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">
          Basic Information
        </h2>
        <p className="text-sm md:text-base text-gray-600">
          Let's start with the essentials of your community
        </p>
      </div>

      {isPublished && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <BsLockFill className="text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <span className="font-semibold">Some fields are locked.</span> Name, artwork, and membership tier are permanent on-chain and cannot be changed after publishing. You can still edit the descriptions, images, and category.
          </p>
        </div>
      )}

      {/* Community Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          Community / Membership Name *
          {isPublished && <BsLockFill className="text-amber-400 text-xs" />}
        </label>
        <input
          type="text"
          name="name"
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="e.g., DeSci Builders Elite"
          disabled={isPublished}
          className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
            formik.touched.name && formik.errors.name
              ? "border-red-500"
              : "border-gray-200"
          } ${isPublished ? "bg-gray-50 opacity-60 cursor-not-allowed" : ""}`}
          maxLength={100}
        />
        {formik.touched.name && formik.errors.name && (
          <p className="mt-1 text-sm text-red-500">{formik.errors.name}</p>
        )}
        <p className="mt-1 text-xs text-gray-500">
          {formik.values.name?.length || 0}/100 characters
        </p>
      </div>

      {/* Short Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Short Description *
        </label>
        <textarea
          name="shortDescription"
          value={formik.values.shortDescription}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="Brief description of your community's purpose and value..."
          rows={3}
          className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none ${
            formik.touched.shortDescription && formik.errors.shortDescription
              ? "border-red-500"
              : "border-gray-200"
          }`}
          maxLength={300}
        />
        {formik.touched.shortDescription && formik.errors.shortDescription && (
          <p className="mt-1 text-sm text-red-500">
            {formik.errors.shortDescription}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-500">
          {formik.values.shortDescription?.length || 0}/300 characters
        </p>
      </div>

      {/* Long Description */}
      <LexicalEditor
        id="longDescription"
        title="Long Description"
        placeholder="Detailed description of perks, rules, community vision, etc..."
        value={formik.values.longDescription || ""}
        onChange={(html) => formik.setFieldValue("longDescription", html)}
        limit={10000}
        containerClass="w-full"
        showImageBtn={false}
      />

      {/* Artwork Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          <span className="flex items-center gap-2">
            <BsImage className="text-primary" />
            NFT Artwork / Image *
            {isPublished && <BsLockFill className="text-amber-400 text-xs" />}
          </span>
        </label>
        <div className="flex flex-col md:flex-row gap-4 md:gap-6">
          <div
            className={`relative w-full md:w-48 h-48 border-2 border-dashed rounded-xl md:rounded-2xl flex flex-col items-center justify-center transition-all ${
              isPublished
                ? "border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed"
                : `cursor-pointer hover:border-primary hover:bg-purple-50 ${
                    formik.values.artwork ? "border-primary bg-purple-50" : "border-gray-300"
                  }`
            }`}
            onClick={() => !isPublished && document.getElementById("artwork-input").click()}
          >
            {formik.values.artwork ? (
              <>
                <img
                  src={formik.values.artwork}
                  alt="Artwork preview"
                  className="w-full h-full object-cover rounded-lg md:rounded-xl"
                />
                {!isPublished && (
                  <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all rounded-lg md:rounded-xl flex items-center justify-center">
                    <FiUpload className="text-white text-2xl opacity-0 hover:opacity-100 transition-opacity" />
                  </div>
                )}
              </>
            ) : uploadingImage ? (
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="mt-2 text-sm text-gray-500">Uploading...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center text-gray-400">
                <FiUpload className="text-3xl md:text-4xl mb-2" />
                <p className="text-sm font-medium">Upload Image</p>
                <p className="text-xs">Min 1000x1000px</p>
              </div>
            )}
            <input
              id="artwork-input"
              type="file"
              accept="image/*"
              disabled={isPublished}
              onChange={(e) => handleImageUpload(e, "artwork")}
              className="hidden"
            />
          </div>
          <div className="flex-1 bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-4 border border-purple-100">
            <div className="flex items-start gap-2 text-purple-700">
              <BsInfoCircle className="text-lg flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold mb-2">🎨 Image Guidelines</p>
                <ul className="text-xs space-y-1.5 text-purple-600">
                  <li>• Minimum resolution: 1000 x 1000 pixels</li>
                  <li>• Square format recommended</li>
                  <li>• Supported formats: PNG, JPG, GIF</li>
                  <li>• Max file size: 5MB</li>
                  <li>• This will be your NFT artwork</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        {formik.touched.artwork && formik.errors.artwork && (
          <p className="mt-2 text-sm text-red-500">{formik.errors.artwork}</p>
        )}
      </div>

      {/* Banner Image (Optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Banner Image <span className="text-gray-400">(Optional)</span>
        </label>
        <div
          className={`relative w-full h-32 border-2 border-dashed rounded-xl flex items-center justify-center cursor-pointer transition-all hover:border-primary hover:bg-purple-50 ${
            formik.values.bannerImage ? "border-primary" : "border-gray-300"
          }`}
          onClick={() => document.getElementById("banner-input").click()}
        >
          {formik.values.bannerImage ? (
            <img
              src={formik.values.bannerImage}
              alt="Banner preview"
              className="w-full h-full object-cover rounded-xl"
            />
          ) : (
            <div className="flex items-center gap-3 text-gray-400">
              <BsImage className="text-2xl" />
              <p className="text-sm">Upload banner image (1400 x 400px recommended)</p>
            </div>
          )}
          <input
            id="banner-input"
            type="file"
            accept="image/*"
            onChange={(e) => handleImageUpload(e, "bannerImage")}
            className="hidden"
          />
        </div>
      </div>

      {/* Category */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Category *
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
          {categories.map((cat) => (
            <button
              key={cat.value}
              type="button"
              onClick={() => formik.setFieldValue("category", cat.value)}
              className={`px-3 md:px-4 py-3 rounded-xl text-xs md:text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                formik.values.category === cat.value
                  ? "bg-gradient-to-br from-primary to-purple-600 text-white shadow-lg scale-105"
                  : "bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200"
              }`}
            >
              <span className="text-base md:text-lg">{cat.icon}</span>
              <span className="truncate">{cat.label}</span>
            </button>
          ))}
        </div>
        {formik.touched.category && formik.errors.category && (
          <p className="mt-2 text-sm text-red-500">{formik.errors.category}</p>
        )}
      </div>

      {/* Membership Tier */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          Membership Tier
          {isPublished && <BsLockFill className="text-amber-400 text-xs" />}
        </label>
        <div className={`flex flex-wrap gap-3 ${isPublished ? "opacity-60 pointer-events-none" : ""}`}>
          {tiers.map((tier) => (
            <button
              key={tier.value}
              type="button"
              onClick={() => !isPublished && formik.setFieldValue("membershipTier", tier.value)}
              disabled={isPublished}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                formik.values.membershipTier === tier.value
                  ? `bg-gradient-to-r ${tier.color} text-white shadow-lg`
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              } ${isPublished ? "cursor-not-allowed" : ""}`}
            >
              {tier.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Step1BasicInfo;
